<template>
  <div class="purchase-detail mt-5 lg:mt-10">
    <the-section :hasSlotTitle="true">
      <template #slotTitle>
        <div class="flex justify-between items-center">
          <h2 class="flex-1">{{ $t('ec-line005.purchaseDetail.title') }}</h2>
          <span class="text-base">{{ $t('ec-line005.purchaseDetail.orderId', { id: orderId }) }}</span>
        </div>
      </template>
      <cart-list-item class="hidden lg:block" :data="data" :isCartPeriodic="isCartPeriodic" disabled></cart-list-item>
      <cart-list-item-sp
        v-for="(item, idx) in data"
        class="block lg:hidden"
        :key="idx"
        :data="item"
      ></cart-list-item-sp>
      <div class="bg-white pt-0">
        <div v-if="summary" class="flex w-full purchase-detail__total">
          <div class="ml-auto text-black mt-4 pl-6 pr-2 py-4 rounded-lg bg-gray">
            <!-- 小計 -->
            <div class="flex justify-between items-center text-base lg:text-lg mb-3">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.subtotal') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
                <el-popover
                  class="ml-2"
                  :tabindex="2"
                  placement="top"
                  title=""
                  width="200"
                  trigger="hover"
                  :content="$t('common.cart.subtotalExplanation')"
                >
                  <span slot="reference">
                    <img :src="require('@/assets/images/help-round-icon.svg')" alt="" />
                  </span>
                </el-popover>
              </div>
              <span>{{ $n(summary.subtotal, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 割引額 -->
            <div v-if="hasDiscountAmount" class="flex justify-between items-center text-base lg:text-lg">
              <div class="flex items-center text-base lg:text-lg">
                <span class="text-xs">{{ $t('common.label.priceDiscount') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span class="text-xs text-red">({{ $n(summary.discount_amount_total, 'currency', 'ja-JP') }})</span>
            </div>
            <!-- 会員ランク割引額 message -->
            <p
              v-if="user && user.rank_name && hasDiscountAmount && user.rank_use_flag"
              class="flex justify-between items-center text-base lg:text-lg"
            >
              <span class="text-xs text-red">{{
                $t('common.messages.rankDiscount', { rankMember: user.rank_name })
              }}</span>
            </p>
            <!-- 配送料金 -->
            <div class="flex justify-between items-center text-base lg:text-lg mt-3 mb-5">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.deliveryFee') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span>{{ $n(summary.delivery_fee, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 商品合計 -->
            <div class="flex font-bold justify-between items-center text-base lg:text-lg mb-3">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.total') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span class="text-2xl">{{ $n(summary.payment_total, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 消費税 -->
            <p class="flex justify-between items-center text-base lg:text-lg">
              <span class="text-xs">{{ $t('cartList.item.consumptionTax') }}</span>
              <span class="text-xs">({{ $n(summary.tax_total, 'currency', 'ja-JP') }})</span>
            </p>
          </div>
        </div>
      </div>
    </the-section>
    <!-- 次回購入内容 -->
    <the-section
      class="block mt-10"
      v-if="isCartPeriodic && isDiscount"
      :title="$t('ec-line005.purchaseDetail.subsequentHeader')"
    >
      <template #subTitle>
        <span class="text-base">{{ $t('ec-line005.purchaseDetail.subsequentSubtitle') }}</span>
      </template>
      <cart-list-item
        class="hidden lg:block"
        :data="data"
        :isCartPeriodic="isCartPeriodic"
        :is-next-subscription="true"
        disabled
      ></cart-list-item>
      <cart-list-item-sp
        v-for="(item, idx) in data"
        class="block lg:hidden"
        :key="idx"
        :data="item"
        :is-next-subscription="true"
      ></cart-list-item-sp>
      <div class="bg-white pt-0 pb-8">
        <div v-if="summary" class="flex w-full purchase-detail__total">
          <div class="ml-auto text-black mt-4 pl-6 pr-2 py-4 rounded-lg bg-gray">
            <!-- 小計 -->
            <div class="flex justify-between items-center text-base lg:text-lg mb-3">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.subtotal') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
                <el-popover
                  class="ml-2"
                  :tabindex="2"
                  placement="top"
                  title=""
                  width="200"
                  trigger="hover"
                  :content="$t('common.cart.subtotalExplanation')"
                >
                  <span slot="reference">
                    <img :src="require('@/assets/images/help-round-icon.svg')" alt="" />
                  </span>
                </el-popover>
              </div>
              <span>{{ $n(summary.next_subtotal, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 割引額 -->
            <div v-if="hasNextDiscountAmount" class="flex justify-between items-center text-base lg:text-lg">
              <div class="flex items-center text-base lg:text-lg">
                <span class="text-xs">{{ $t('common.label.priceDiscount') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span class="text-xs text-red">({{ $n(summary.next_discount_amount_total, 'currency', 'ja-JP') }})</span>
            </div>
            <!-- 会員ランク割引額 message -->
            <p
              v-if="user && user.rank_name && hasNextDiscountAmount && user.rank_use_flag"
              class="flex justify-between items-center text-base lg:text-lg"
            >
              <span class="text-xs text-red">{{
                $t('common.messages.rankDiscount', { rankMember: user.rank_name })
              }}</span>
            </p>
            <!-- 配送料金 -->
            <div class="flex justify-between items-center text-base lg:text-lg mt-3 mb-5">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.deliveryFee') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span>{{ $n(summary.next_delivery_fee, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 商品合計 -->
            <div class="flex font-bold justify-between items-center text-base lg:text-lg mb-3">
              <div class="flex items-center text-base lg:text-lg">
                <span>{{ $t('common.label.total') }}</span>
                <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
              </div>
              <span class="text-2xl">{{ $n(summary.next_payment_total, 'currency', 'ja-JP') }}</span>
            </div>
            <!-- 消費税 -->
            <p class="flex justify-between items-center text-base lg:text-lg">
              <span class="text-xs">{{ $t('cartList.item.consumptionTax') }}</span>
              <span class="text-xs">({{ $n(summary.next_tax_total, 'currency', 'ja-JP') }})</span>
            </p>
          </div>
        </div>
      </div>
    </the-section>
  </div>
</template>

<script>
import TheSection from '@/components/my-page-partials/TheSection.vue'
import CartListItem from '@/components/CartListItem.vue'
import CartListItemSp from '@/components/CartListItemSp.vue'

export default {
  props: {
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    summary: {
      type: Object,
      required: false,
      default: () => {},
    },
    isCartPeriodic: Boolean,
    orderId: {
      type: String,
      required: true,
      default: () => '',
    },
  },
  components: { TheSection, CartListItem, CartListItemSp },
  computed: {
    hasDiscountAmount() {
      return this.summary.discount_amount_total && Number(this.summary.discount_amount_total) < 0
    },
    hasNextDiscountAmount() {
      return this.summary.next_discount_amount_total && Number(this.summary.next_discount_amount_total) < 0
    },
    isDiscount() {
      return this.data.some((item) => item.discountAmount && Number(item.discountAmount) < 0)
    },
  },
}
</script>
